import _ from "lodash";
import moment from "moment";

import { inputStateForValue, sumFloatingPointNumbers, sumNumbers } from "utils";

import {
  ACCORDION_SECTIONS,
  COUNTRY_CODE,
  DAYS_PER_YEAR,
  DEFAULT_DAIRY_FOOTPRINT_KGCO2EQ,
  DEFAULT_PIGLET_FOOTPRINT_KGCO2EQ_PER_KG_LW,
  DEFAULT_PORK_FOOTPRINT_KGCO2EQ_PER_KG_SW,
  ENUM_RESULTS_PAGE_TYPE,
  InputState,
  KG_PER_TONNE,
  LFL_DEFAULT_CLEANING_TIME_DAYS,
  PORK_PIGLET_CAPACITY_LOWER_BOUND_MULTIPLIER,
  PORK_PIGLET_CAPACITY_UPPER_BOUND_MULTIPLIER,
  REGIONAL_COMPARISON_CROP_MAPPINGS,
  RESULTS_PAGE_TYPES,
} from "consts";

import {
  AggregatedCropFootprint,
  BeefFootprintCalculationSummary,
  BeefFootprintReturnBody,
  BeefResultsSubDataRowSource,
  CalculatedBeefFootprintResponse,
  CalculatedCropFootprintResponse,
  CalculatedDairyFootprintResponse,
  CalculatedFarmFootprintResponse,
  CalculatedPigletFootprintResponse,
  CalculatedPorkFootprintResponse,
  CommodityWasteFootprint,
  CropFootprintCalculationSummary,
  CropFootprintSummaryMetrics,
  CropResultsSubDataRowSource,
  DairyFootprintCalculationSummary,
  DairyFootprintReturnBody,
  DairyResultsSubDataRowSource,
  FOOTPRINTING_BANNER_KEYS,
  FarmDetailsResponse,
  FarmOrganicFertilizerProductionEntry,
  FieldPracticeResponse,
  FootprintingBeefResultsDataTableRow,
  FootprintingBeefResultsSubDataRowData,
  FootprintingBeefResultsTableData,
  FootprintingCropResultsSubDataRowData,
  FootprintingCropResultsTableData,
  FootprintingCropResultsTableRowData,
  FootprintingDairyResultsSubDataRowData,
  FootprintingDairyResultsTableData,
  FootprintingDairyResultsTableRowData,
  FootprintingFootprintAggregation,
  FootprintingPigletResultsTableData,
  FootprintingPigletResultsTableRowData,
  FootprintingPorkResultsTableData,
  FootprintingPorkResultsTableRowData,
  PRACTICE_SLUG,
  PigletFootprintCalculationSummary,
  PorkFootprintCalculationSummary,
  PracticeCrop,
} from "models";

import { CooperationValues, FertilizerSourceValues, OwnProductionValues } from "pages/app/all-field-view/components/tasksDrawer/FarmSourcesDrawer";

export function bannerKey(farmId: string, year: number, bannerType: (typeof FOOTPRINTING_BANNER_KEYS)[keyof typeof FOOTPRINTING_BANNER_KEYS]) {
  return `${year}-${farmId}-${bannerType}-complete-banner-shown`;
}

/**
 * Safely extract emission values with null checks
 * @param footprint - The footprint aggregation object
 * @param type - The type of value to extract (avg, min, max)
 * @returns The emission value or 0 if not available
 */
const extractEmissionValue = (footprint: FootprintingFootprintAggregation | undefined, type: "avg" | "min" | "max" = "avg"): number =>
  footprint === undefined ? 0 : (footprint.amount.emission[type] ?? 0) + (footprint.penalty.emission[type] ?? 0);

/**
 * Livestock results have no wastewater row, so a commodity's waste and wastewater are reported together
 * in its waste row. PEAS already attributes both to the commodity, so no allocation applies.
 */
const extractCommodityWasteEmission = (waste: CommodityWasteFootprint | undefined): number =>
  sumNumbers([extractEmissionValue(waste?.wasteFootprint), extractEmissionValue(waste?.wastewaterFootprint)]);

const createCropDataRow = (sourceEmissions: number, cropHas: number, cropYield: number, totalEmissions: number): FootprintingCropResultsTableRowData => ({
  totalCO2e: sourceEmissions,
  perHaCO2e: sourceEmissions / cropHas,
  perKgYieldCO2e: sourceEmissions / cropYield,
  shareOfTotal: _.round(sourceEmissions / totalEmissions, 4),
});

const createCropSubdataRow = (
  source: CropResultsSubDataRowSource,
  sourceEmissions: number,
  cropHa: number,
  cropYield: number,
  totalEmissions: number
): FootprintingCropResultsSubDataRowData => ({
  source,
  ...createCropDataRow(sourceEmissions, cropHa, cropYield, totalEmissions),
});

const EMPTY_SOURCE_ROW = { data: createCropDataRow(0, 1, 1, 1), subData: [] };
const EMPTY_SUMMARY: CropFootprintCalculationSummary = {
  overall: { totalCO2e: 0, perKgYieldCO2e: 0, perHaCO2e: 0 },
  tableData: {
    fertilizer: { ...EMPTY_SOURCE_ROW },
    plantProtection: { ...EMPTY_SOURCE_ROW },
    transportation: { ...EMPTY_SOURCE_ROW },
    fieldWork: { ...EMPTY_SOURCE_ROW },
    energy: { ...EMPTY_SOURCE_ROW },
    waste: { ...EMPTY_SOURCE_ROW },
    residue: { ...EMPTY_SOURCE_ROW },
    luc: { ...EMPTY_SOURCE_ROW },
    other: { ...EMPTY_SOURCE_ROW },
  },
};

/**
 * Comprehensive data processor for footprint results page
 * Transforms raw farm and crop footprint data into structured format for all UI components
 * @param farmFootprintData - The calculated farm footprint data from the API
 * @param cropFootprintData - Array of calculated crop footprint data
 * @param farmSize - Total farm size in hectares (for per-hectare calculations)
 * @param totalYield - Total crop yield in kg (for per-yield calculations)
 * @returns Structured data for all UI components (summary cards, pie chart, table)
 */
export function createFootprintCalculationSummary(
  farm: FarmDetailsResponse,
  farmCalculation: CalculatedFarmFootprintResponse | undefined,
  cropCalculation: CalculatedCropFootprintResponse | undefined,
  cropFootprint: AggregatedCropFootprint | undefined
): CropFootprintCalculationSummary | null {
  if (!farmCalculation || !cropCalculation || !cropFootprint) {
    return _.cloneDeep(EMPTY_SUMMARY);
  }

  const farmSize = farm.size ?? 1;
  const totalCropHectars = cropFootprint.totalFieldHectares ?? 1;
  const normalizedCropYieldKgs = KG_PER_TONNE * (cropFootprint.normalizedWeightTs ?? 0);

  if (totalCropHectars < 0.001 || normalizedCropYieldKgs < 0.1) {
    return null;
  }

  const hectarRatio = totalCropHectars / farmSize;

  /** FARM ENERGYs */
  const farmDieselEmissions = extractEmissionValue(farmCalculation.calculatedFootprint.energy.dieselFootprint) * hectarRatio;
  const farmBenzinEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.benzinFootprint) * hectarRatio;
  const farmNaturalGasEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.naturalGasFootprint) * hectarRatio;
  const farmHeatingOilEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.heatingOilFootprint) * hectarRatio;
  const farmDirtyElectricityEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.electricityFootprint) * hectarRatio;
  const farmRenewableElectricityEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.renewableElectricityFootprint) * hectarRatio;
  const farmElectricityEmission = sumNumbers([farmDirtyElectricityEmission, farmRenewableElectricityEmission]);
  const totalFarmEnergyEmission = sumNumbers([
    farmDieselEmissions,
    farmBenzinEmission,
    farmNaturalGasEmission,
    farmHeatingOilEmission,
    farmElectricityEmission,
  ]);

  /** LAND USE CHANGE */
  const totalLandUseChangeEmission = farmCalculation.calculatedFootprint.misc.landUseChangeFooprint.reduce((sum, luc) => {
    const biomassEmission = extractEmissionValue(luc.footprints.biomassEmissionFootprint) * hectarRatio;
    const deadOrganicMatterEmission = extractEmissionValue(luc.footprints.deadOrganicMatterFootprint) * hectarRatio;
    const socChangeEmission = extractEmissionValue(luc.footprints.socChangeFootprint) * hectarRatio;
    return sum + biomassEmission + deadOrganicMatterEmission + socChangeEmission;
  }, 0);

  /** FERTILIZERS && PESTICIDES */
  const cropOrganicFertilizerEmissions = sumNumbers([
    ...cropCalculation.calculatedFootprint.organicFertilizerFootprint.NH3VolatilizationCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.organicFertilizerFootprint.leachedNitrateCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.organicFertilizerFootprint.organicFertilizerDirectCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
  ]);

  const cropMineralFertilizerEmissions = sumNumbers([
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.NH3VolatilizationCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.leachedNitrateCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.directCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.productionCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.totalNeutralisation.map(({ emissions }) => extractEmissionValue(emissions)),
  ]);

  const cropLimeFertilizerEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.agriculturalLimeFootprint.emissions);

  const totalFertilizerEmissions = sumNumbers([cropOrganicFertilizerEmissions, cropMineralFertilizerEmissions, cropLimeFertilizerEmissions]);

  /** PLANT PROTECTIONS */
  const cropPlantProtectionEmissions = sumNumbers([
    ...cropCalculation.calculatedFootprint.plantProtectionFootprint.map(({ emissions }) => extractEmissionValue(emissions)),
  ]);
  const totalPlantProtectionEmissions = cropPlantProtectionEmissions; // Trust the naming scheme

  /** TRANSPORTATION */
  const cropUpstreamTransportEmission = sumNumbers([
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.mineralFertilizers.map(({ footprint }) => extractEmissionValue(footprint)),
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.organicFertilizers.map(({ footprint }) => extractEmissionValue(footprint)),
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.plantProtection.map(({ footprint }) => extractEmissionValue(footprint)),
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.seeding.map(({ footprint }) => extractEmissionValue(footprint)),
  ]);
  const cropDownstreamTransportEmission = sumNumbers(
    cropCalculation.calculatedFootprint.downstreamTransportationFootprint.crop.map(({ footprint }) => extractEmissionValue(footprint))
  );

  const cropTotalTransportationEmissions = cropUpstreamTransportEmission + cropDownstreamTransportEmission;

  /** FIELD WORK  */
  const cropFieldWorkHarvestingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dieselFootprint.harvestDieselUsage.footprint);
  const cropFieldWorkFertilizerApplicationEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.fertilizerApplication.map(({ footprint }) => extractEmissionValue(footprint))
  );
  const cropFieldWorkPlantProtectionApplicationsEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.plantProtectionApplications.map(({ footprint }) => extractEmissionValue(footprint))
  );
  const cropFieldWorkSeedingApplicationEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.seedingApplication.map(({ footprint }) => extractEmissionValue(footprint))
  );
  const cropFieldWorkTillageApplicationEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.tillageApplication.map(({ footprint }) => extractEmissionValue(footprint))
  );

  const totalFieldWorkEmissions = sumNumbers([
    cropFieldWorkHarvestingEmissions,
    cropFieldWorkFertilizerApplicationEmissions,
    cropFieldWorkPlantProtectionApplicationsEmissions,
    cropFieldWorkSeedingApplicationEmissions,
    cropFieldWorkTillageApplicationEmissions,
  ]);

  /** CROP RESIDUE */
  const residueDirectEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.cropResidueFootprint.directN2OCO2Eq);
  const residueLeachedNitrateEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.cropResidueFootprint.leachedNitrateCO2Eq);
  const totalResidueEmissions = sumNumbers([residueDirectEmissions, residueLeachedNitrateEmissions]);

  /** CROP Irrigation */
  const cropIrrigationEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.irrigationFootprint);

  /** CROP WASTEWATER & WASTE */
  const cropWastewaterEmission = extractEmissionValue(cropCalculation.calculatedFootprint.waste?.wastewaterFootprint);
  const totalWasteEmission = extractEmissionValue(cropCalculation.calculatedFootprint.waste?.wasteFootprint);

  /** OTHER */
  const totalWaterEmissions = sumNumbers([cropWastewaterEmission, cropIrrigationEmissions]);
  const totalOtherEmissions = totalWaterEmissions;

  const totalEmissions = sumNumbers([
    totalFertilizerEmissions,
    totalPlantProtectionEmissions,
    cropTotalTransportationEmissions,
    totalFieldWorkEmissions,
    totalFarmEnergyEmission,
    totalWasteEmission,
    totalResidueEmissions,
    totalLandUseChangeEmission,
    totalOtherEmissions,
  ]);

  const tableData: FootprintingCropResultsTableData = {
    fertilizer: {
      data: createCropDataRow(totalFertilizerEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [
        createCropSubdataRow("mineralFertilizers", cropMineralFertilizerEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("organicFertilizers", cropOrganicFertilizerEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("lime", cropLimeFertilizerEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      ],
    },
    plantProtection: {
      data: createCropDataRow(cropPlantProtectionEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [],
    },
    transportation: {
      data: createCropDataRow(cropTotalTransportationEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [
        createCropSubdataRow("upstreamTransportation", cropUpstreamTransportEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("downstreamTransportation", cropDownstreamTransportEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      ],
    },
    fieldWork: {
      data: createCropDataRow(totalFieldWorkEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [
        createCropSubdataRow("harvesting", cropFieldWorkHarvestingEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("fertilizerApplication", cropFieldWorkFertilizerApplicationEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow(
          "plantProtectionApplications",
          cropFieldWorkPlantProtectionApplicationsEmissions,
          totalCropHectars,
          normalizedCropYieldKgs,
          totalEmissions
        ),
        createCropSubdataRow("seedingApplication", cropFieldWorkSeedingApplicationEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("tillageApplication", cropFieldWorkTillageApplicationEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      ],
    },
    energy: {
      data: createCropDataRow(totalFarmEnergyEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [
        createCropSubdataRow("diesel", farmDieselEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("benzin", farmBenzinEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("naturalGas", farmNaturalGasEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("heating", farmHeatingOilEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
        createCropSubdataRow("electricity", farmElectricityEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      ],
    },
    waste: {
      data: createCropDataRow(totalWasteEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [],
    },
    residue: {
      data: createCropDataRow(totalResidueEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [],
    },
    luc: {
      data: createCropDataRow(totalLandUseChangeEmission, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [],
    },
    other: {
      data: createCropDataRow(totalOtherEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions),
      subData: [createCropSubdataRow("water", totalWaterEmissions, totalCropHectars, normalizedCropYieldKgs, totalEmissions)],
    },
  };

  return {
    tableData,
    overall: {
      totalCO2e: totalEmissions,
      perKgYieldCO2e: totalEmissions / normalizedCropYieldKgs,
      perHaCO2e: totalEmissions / totalCropHectars,
    },
  };
}

function addCropFootprintSummaryMetrics(
  a: CropFootprintSummaryMetrics,
  b: CropFootprintSummaryMetrics,
  totalYieldKg: number,
  totalFieldHa: number
): CropFootprintSummaryMetrics {
  return {
    totalCO2e: _.round(sumFloatingPointNumbers(a.totalCO2e, b.totalCO2e), 3),
    perKgYieldCO2e: _.round(sumFloatingPointNumbers(a.totalCO2e, b.totalCO2e) / totalYieldKg, 3),
    perHaCO2e: _.round(sumFloatingPointNumbers(a.totalCO2e, b.totalCO2e) / totalFieldHa, 3),
  };
}

/**
 * Totals the yield and hectares across crops, for the combined "all crops" footprint-per-kg figure.
 *
 * `totalWeightTs` is already the crop's whole harvest in tonnes: the API multiplies the per-hectare value
 * by the crop's hectares and normalizes the farmer's chosen unit to tonnes (`aggregateCropFootprintData`).
 * So summing it is the entire job — do not multiply by hectares again, and do not convert units here.
 */
export const sumCropYieldTonnesAndHectares = (
  footprints: (Pick<AggregatedCropFootprint, "totalWeightTs" | "totalFieldHectares"> | undefined)[]
): { totalYieldTonnes: number; totalFootprintedHectares: number } =>
  footprints.reduce(
    (totals, footprint) => ({
      totalYieldTonnes: totals.totalYieldTonnes + (footprint?.totalWeightTs ?? 0),
      totalFootprintedHectares: totals.totalFootprintedHectares + (footprint?.totalFieldHectares ?? 0),
    }),
    { totalYieldTonnes: 0, totalFootprintedHectares: 0 }
  );

export function sumFootprintCalculationSummaries(
  summaries: CropFootprintCalculationSummary[],
  totalYieldKg: number,
  totalFieldHa: number
): CropFootprintCalculationSummary {
  return summaries.reduce(
    (total, fcs) => {
      const overall = addCropFootprintSummaryMetrics(total.overall, fcs.overall, totalYieldKg, totalFieldHa);
      return {
        overall,
        tableData: {
          fertilizer: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.fertilizer.data.totalCO2e, fcs.tableData.fertilizer.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.fertilizer.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.fertilizer.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          plantProtection: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.plantProtection.data.totalCO2e, fcs.tableData.plantProtection.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.plantProtection.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.plantProtection.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          transportation: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.transportation.data.totalCO2e, fcs.tableData.transportation.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.transportation.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.transportation.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          fieldWork: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.fieldWork.data.totalCO2e, fcs.tableData.fieldWork.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.fieldWork.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.fieldWork.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          energy: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.energy.data.totalCO2e, fcs.tableData.energy.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.energy.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.energy.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          waste: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.waste.data.totalCO2e, fcs.tableData.waste.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.waste.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.waste.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          residue: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.residue.data.totalCO2e, fcs.tableData.residue.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.residue.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.residue.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          luc: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.luc.data.totalCO2e, fcs.tableData.luc.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.luc.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.luc.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          other: {
            data: createCropDataRow(
              sumFloatingPointNumbers(total.tableData.other.data.totalCO2e, fcs.tableData.other.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.other.subData.map((sdr): FootprintingCropResultsSubDataRowData => {
              const otherRow = total.tableData.other.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createCropDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
        },
      };
    },
    _.clone(EMPTY_SUMMARY) as CropFootprintCalculationSummary
  );
}

export const getMidpointGrowingDate = (crop?: PracticeCrop, year: number = new Date().getFullYear()): Date => {
  if (!crop?.sequestrationValues?.[0]) {
    return moment().year(year).endOf("year").toDate();
  }

  const sequestrationValues = crop?.sequestrationValues?.[0];
  const seedingStartDate = moment(sequestrationValues?.seedingStart, "DD/MM").year(year);
  const harvestEndDate = moment(sequestrationValues?.harvestEnd, "DD/MM").year(year);

  if (seedingStartDate.isAfter(harvestEndDate)) {
    seedingStartDate.subtract(1, "year");
  }

  return seedingStartDate
    .clone()
    .add(harvestEndDate.diff(seedingStartDate) / 2)
    .toDate();
};

const createDairyDataRow = (sourceEmissions: number, cowCount: number, milkYield: number, totalEmissions: number): FootprintingDairyResultsTableRowData => ({
  totalCO2e: sourceEmissions,
  perCowCO2e: sourceEmissions / cowCount,
  perKgYieldCO2e: sourceEmissions / milkYield,
  shareOfTotal: _.round(sourceEmissions / totalEmissions, 4),
});

const createDairySubdataRow = (
  source: DairyResultsSubDataRowSource,
  sourceEmissions: number,
  cowCount: number,
  milkYield: number,
  totalEmissions: number
): FootprintingDairyResultsSubDataRowData => ({
  source,
  ...createDairyDataRow(sourceEmissions, cowCount, milkYield, totalEmissions),
});

/**
 * Comprehensive data processor for footprint dairy results
 * Transforms raw farm and dairy footprint data into structured format for all UI components
 * @returns Structured data for all UI components (summary cards, pie chart, table)
 */
export function createDairyFootprintCalculationSummary(
  _farm: FarmDetailsResponse,
  farmCalculation: CalculatedFarmFootprintResponse | undefined,
  dairyCalculation: CalculatedDairyFootprintResponse | undefined,
  dairyFootprint: DairyFootprintReturnBody | undefined
): DairyFootprintCalculationSummary | null {
  const cowCount = dairyFootprint?.footprint?.herdSize.reduce((total, { cowCount }) => total + (cowCount ?? 0), 0) ?? 0;
  const fcpmMilkYieldKgsPerCow = dairyFootprint?.footprint?.fcpmCorrectedMilkYieldKgPerCow ?? null;

  if (!_farm || !farmCalculation || !dairyCalculation || cowCount === 0 || !fcpmMilkYieldKgsPerCow) return null;

  const fcpmMilkYieldKgs = fcpmMilkYieldKgsPerCow * cowCount;

  /** DAIRY REFRIGERANTS (from PEAS) */
  const totalRefrigerantUsageEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.misc?.refrigerants);

  /** LAND USE CHANGE */
  const totalLandUseChangeEmissions = farmCalculation.calculatedFootprint.misc.landUseChangeFooprint.reduce((sum, luc) => {
    const biomassEmission = extractEmissionValue(luc.footprints.biomassEmissionFootprint);
    const deadOrganicMatterEmission = extractEmissionValue(luc.footprints.deadOrganicMatterFootprint);
    const socChangeEmission = extractEmissionValue(luc.footprints.socChangeFootprint);
    return sum + biomassEmission + deadOrganicMatterEmission + socChangeEmission;
  }, 0);

  /** DAIRY WASTE & WASTEWATER */
  const totalWasteEmissions = extractCommodityWasteEmission(dairyCalculation.calculatedFootprint.waste);

  /** DAIRY STOCK RENEWAL */
  const totalStockRenewalEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.stockRenewal);

  /** DAIRY CALF REARING */
  const calvesDigestionEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.calves?.feeding?.digestionCO2eq);
  const calvesFeedProductionEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.calves?.feeding?.production);
  const calvesManureManagementEmissions = sumNumbers([
    extractEmissionValue(dairyCalculation.calculatedFootprint.calves?.manureManagement.storageCO2eq),
    extractEmissionValue(dairyCalculation.calculatedFootprint.calves?.manureManagement.storageDirectCO2eq),
    extractEmissionValue(dairyCalculation.calculatedFootprint.calves?.manureManagement.storageIndirectCO2eq),
  ]);
  const totalCalfRearingEmissions = sumNumbers([calvesDigestionEmissions, calvesFeedProductionEmissions, calvesManureManagementEmissions]);

  /** DAIRY COW FEEDING EMISSIONS */
  const cowsDigestionEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.feeding?.digestionCO2eq);
  const cowsFeedProductionEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.feeding?.production);
  const cowsFeedLossEmissions = sumNumbers([
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.feeding?.feedLossDirectCO2eq),
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.feeding?.feedLossIndirectCO2eq),
  ]);
  const totalCowFeedingEmissions = sumNumbers([cowsDigestionEmissions, cowsFeedProductionEmissions, cowsFeedLossEmissions]);

  /** DAIRY COW MANURE MANAGEMENT */
  const cowManureStorageEmissions = sumNumbers([
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.manureManagement?.storageCO2eq),
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.manureManagement?.storageIndirectCO2eq),
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.manureManagement?.storageDirectCO2eq),
  ]);
  const cowManurePastureEmissions = sumNumbers([
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.pasture?.pastureCO2eq),
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.pasture?.pastureDirectCO2eq),
    extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.pasture?.pastureIndirectCO2eq),
  ]);
  const cowManureBeddingEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.dairyCows?.bedding);
  const totalCowManureEmissions = sumNumbers([cowManureStorageEmissions, cowManurePastureEmissions, cowManureBeddingEmissions]);

  /** DAIRY WATER EMISSIONS */
  const totalWaterEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.waterUsage);

  /** DAIRY ENERGY EMISSIONS */
  const nonDieselEnergyEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.energy);
  const dieselEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.diesel);
  const totalEnergyEmissions = sumNumbers([nonDieselEnergyEmissions, dieselEmissions]);

  /** DAIRY MACHINERY EMISSIONS */
  const totalMachineryEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.machinery);

  /** DAIRY GRASSLAND FERTILISER (CFP only — N2O and production emissions from fertiliser applied to grazed grassland) */
  const totalGrasslandFertiliserEmissions = extractEmissionValue(dairyCalculation.calculatedFootprint.misc?.fertiliser);

  const totalEmissions = sumNumbers([
    totalWasteEmissions,
    totalRefrigerantUsageEmissions,
    totalLandUseChangeEmissions,
    totalStockRenewalEmissions,
    totalCalfRearingEmissions,
    totalCowFeedingEmissions,
    totalCowManureEmissions,
    totalWaterEmissions,
    totalEnergyEmissions,
    totalMachineryEmissions,
    totalGrasslandFertiliserEmissions,
  ]);

  const tableData: FootprintingDairyResultsTableData = {
    stockRenewal: {
      data: createDairyDataRow(totalStockRenewalEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [],
    },
    calfRearing: {
      data: createDairyDataRow(totalCalfRearingEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [
        createDairySubdataRow("digestion", calvesDigestionEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
        createDairySubdataRow("feedProduction", calvesFeedProductionEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
        createDairySubdataRow("manureManagement", calvesManureManagementEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      ],
    },
    feeding: {
      data: createDairyDataRow(totalCowFeedingEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [
        createDairySubdataRow("digestion", cowsDigestionEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
        createDairySubdataRow("feedLoss", cowsFeedLossEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
        createDairySubdataRow("feedProduction", cowsFeedProductionEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      ],
    },
    manure: {
      data: createDairyDataRow(totalCowManureEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [
        createDairySubdataRow("manureManagement", cowManureStorageEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
        createDairySubdataRow("manurePasture", cowManurePastureEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
        createDairySubdataRow("manureBedding", cowManureBeddingEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      ],
    },
    refrigerants: {
      data: createDairyDataRow(totalRefrigerantUsageEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [],
    },
    waste: {
      data: createDairyDataRow(totalWasteEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [],
    },
    water: {
      data: createDairyDataRow(totalWaterEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [],
    },
    luc: {
      data: createDairyDataRow(totalLandUseChangeEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [],
    },
    energy: {
      data: createDairyDataRow(totalEnergyEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [
        createDairySubdataRow("nonDiesel", nonDieselEnergyEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
        createDairySubdataRow("diesel", dieselEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      ],
    },
    machinery: {
      data: createDairyDataRow(totalMachineryEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [],
    },
    grasslandFertiliser: {
      data: createDairyDataRow(totalGrasslandFertiliserEmissions, cowCount, fcpmMilkYieldKgs, totalEmissions),
      subData: [],
    },
  };

  return {
    tableData,
    overall: {
      totalCO2e: totalEmissions,
      perCowCO2e: totalEmissions / cowCount,
      perKgYieldCO2e: totalEmissions / fcpmMilkYieldKgs,
    },
  };
}

/**
 * Pigs a fattening herd produces over a year: fattening places × batches per place. A place is
 * occupied for the rearing period and then stands empty for cleaning before the next batch arrives,
 * so the cleaning gap belongs in the denominator. Mirrors the formula PEAS uses when building the LfL
 * request (footprint-calculations-peas: lflPorkService pigsProducedPerYear), so nothing in the pork
 * flow disagrees with the calculated result on how many pigs a year of production amounts to.
 *
 * The division is deliberately evaluated before the multiplication, matching PEAS operation for
 * operation. Folding it into `(averageHerdSize * DAYS_PER_YEAR) / days` is algebraically identical but
 * not identical in floating point, and the difference is enough to round to a different whole pig.
 */
export const getPorkPigsProducedPerYear = (averageHerdSize: number, rearingPeriodDays: number): number =>
  averageHerdSize * (DAYS_PER_YEAR / (rearingPeriodDays + LFL_DEFAULT_CLEANING_TIME_DAYS));

/**
 * A null capacity means it could not be established, so no sanity check can be offered. A null
 * state with a capacity means nothing has been entered to check yet.
 */
export type PorkPigletCapacityCheck = { capacity: null; state: null } | { capacity: number; state: InputState | null };

/**
 * Compares piglets purchased over the year against the number the herd can take in over the same
 * period. Losses deliberately play no part — a purchased piglet occupies a place on arrival, so the
 * comparison is intake against intake.
 *
 * Both herd inputs are captured on the herd details step, so a missing or non-positive value means
 * that step is incomplete. No capacity is guessed in that case: a sanity check against an assumed
 * herd would flag figures that are actually correct, which is worse than offering no check.
 */
export const getPorkPigletCapacityCheck = (
  averageHerdSize: number | null | undefined,
  rearingPeriodDays: number | null | undefined,
  totalPurchased: number
): PorkPigletCapacityCheck => {
  if (averageHerdSize == null || averageHerdSize <= 0 || rearingPeriodDays == null || rearingPeriodDays <= 0) return { capacity: null, state: null };

  const capacity = Math.round(getPorkPigsProducedPerYear(averageHerdSize, rearingPeriodDays));

  if (totalPurchased <= 0) return { capacity, state: null };

  /**
   * There is one threshold either side of capacity and neither is hard: a figure outside them is
   * unlikely, not impossible, and nothing about this step blocks on it. So both hard bounds are
   * infinite, and only the soft pair carries the multipliers.
   *
   * The severity the farmer sees — red above capacity, amber below — is a property of the message, not
   * of the number, and lives in the banners the sourcing form renders. Encoding it here instead would
   * claim an over-purchase is invalid, which is the one thing this check cannot establish.
   */
  const bounds: PracticeManagement.ParameterBounds = [
    -Infinity,
    capacity * PORK_PIGLET_CAPACITY_LOWER_BOUND_MULTIPLIER,
    capacity * PORK_PIGLET_CAPACITY_UPPER_BOUND_MULTIPLIER,
    Infinity,
  ];

  return { capacity, state: inputStateForValue(totalPurchased, bounds) };
};

export function createPorkFootprintCalculationSummary(
  _farm: FarmDetailsResponse,
  farmCalculation: CalculatedFarmFootprintResponse | undefined,
  porkCalculation: CalculatedPorkFootprintResponse | undefined
): PorkFootprintCalculationSummary | null {
  if (!_farm || !farmCalculation || !porkCalculation?.calculatedFootprint?.total) return null;

  const { total, feed, husbandry, energy } = porkCalculation.calculatedFootprint;

  const peasTotal = total.CO2eq;
  if (peasTotal === 0) return null;

  /**
   * Weights are derived from the PEAS per-kg intensities, which carry no time basis and so divide
   * cleanly into the annual total. The pig count is taken from PEAS rather than derived: dividing by
   * CO2eqPerPlacePerYear would give fattening places, and emissions per place per year is not the
   * same thing as emissions per pig.
   */
  const totalKgCarcassWeight = peasTotal / total.CO2eqPerKgCarcassWeight;
  const totalKgLiveWeight = peasTotal / total.CO2eqPerKgLiveWeight;
  const { pigsProducedPerYear } = total;

  /** LAND USE CHANGE */
  const totalLucEmissions = farmCalculation.calculatedFootprint.misc.landUseChangeFooprint.reduce((sum, luc) => {
    const biomassEmission = extractEmissionValue(luc.footprints.biomassEmissionFootprint);
    const deadOrganicMatterEmission = extractEmissionValue(luc.footprints.deadOrganicMatterFootprint);
    const socChangeEmission = extractEmissionValue(luc.footprints.socChangeFootprint);
    return sum + biomassEmission + deadOrganicMatterEmission + socChangeEmission;
  }, 0);

  /** PORK WASTE & WASTEWATER */
  const totalWasteEmissions = extractCommodityWasteEmission(porkCalculation.calculatedFootprint.waste);

  const feedingCO2e = extractEmissionValue(feed?.feeding?.feedCO2eq);
  const manureManagementCO2e = extractEmissionValue(husbandry?.manureManagement?.manureCO2eq);
  const stockRenewalCO2e = extractEmissionValue(husbandry?.stockRenewal?.stockRenewalCO2eq);
  const energyCO2e = extractEmissionValue(energy);

  const totalCO2e = sumNumbers([feedingCO2e, manureManagementCO2e, stockRenewalCO2e, energyCO2e, totalWasteEmissions, totalLucEmissions]);

  if (totalCO2e === 0) return null;

  const createPorkDataRow = (sourceEmissions: number): FootprintingPorkResultsTableRowData => ({
    totalCO2e: sourceEmissions,
    perKgYieldCO2e: sourceEmissions / totalKgCarcassWeight,
    perPigCO2e: sourceEmissions / pigsProducedPerYear,
    shareOfTotal: _.round(sourceEmissions / totalCO2e, 4),
  });

  const tableData: FootprintingPorkResultsTableData = {
    feeding: { data: createPorkDataRow(feedingCO2e) },
    manureManagement: { data: createPorkDataRow(manureManagementCO2e) },
    stockRenewal: { data: createPorkDataRow(stockRenewalCO2e) },
    energy: { data: createPorkDataRow(energyCO2e) },
    waste: { data: createPorkDataRow(totalWasteEmissions) },
    luc: { data: createPorkDataRow(totalLucEmissions) },
  };

  return {
    overall: {
      totalCO2e,
      perKgYieldCO2e: totalCO2e / totalKgCarcassWeight,
      perKgLiveWeightCO2e: totalCO2e / totalKgLiveWeight,
      perPigCO2e: totalCO2e / pigsProducedPerYear,
    },
    tableData,
  };
}

/**
 * Resolves the regional reference value the results page compares a farm's own result against.
 * Each footprint type is keyed to the basis its result is reported in — pork per kg slaughter
 * weight, piglet per kg piglet live weight — so the comparison stays like-for-like. Returns
 * undefined when no reference exists for the type or country.
 */
export function getRegionalComparisonValue(
  footprintType: ENUM_RESULTS_PAGE_TYPE | null,
  country: COUNTRY_CODE | null | undefined,
  selectedCropId: string | null
): number | null | undefined {
  switch (footprintType) {
    case RESULTS_PAGE_TYPES.dairy:
      return DEFAULT_DAIRY_FOOTPRINT_KGCO2EQ[country ?? ""];
    case RESULTS_PAGE_TYPES.pork:
      return DEFAULT_PORK_FOOTPRINT_KGCO2EQ_PER_KG_SW[country ?? ""];
    case RESULTS_PAGE_TYPES.piglet:
      return DEFAULT_PIGLET_FOOTPRINT_KGCO2EQ_PER_KG_LW[country ?? ""];
    case RESULTS_PAGE_TYPES.crop:
      return REGIONAL_COMPARISON_CROP_MAPPINGS.find((c) => c.id === selectedCropId)?.regionalCropComparisonValueCO2ekg;
    default:
      return undefined;
  }
}

export function createPigletFootprintCalculationSummary(
  _farm: FarmDetailsResponse,
  farmCalculation: CalculatedFarmFootprintResponse | undefined,
  pigletCalculation: CalculatedPigletFootprintResponse | undefined
): PigletFootprintCalculationSummary | null {
  if (!_farm || !farmCalculation || !pigletCalculation?.calculatedFootprint?.total) return null;

  const { total, feed, husbandry, energy } = pigletCalculation.calculatedFootprint;

  const peasTotal = total.CO2eq;
  if (peasTotal === 0) return null;

  /** Denominators derived from PEAS per-unit metrics */
  const totalKgSalesWeight = total.CO2eqPerKgSalesWeight > 0 ? peasTotal / total.CO2eqPerKgSalesWeight : 1;
  const totalSows = total.CO2eqPerSowPerYear > 0 ? peasTotal / total.CO2eqPerSowPerYear : 1;

  /** LAND USE CHANGE */
  const totalLucEmissions = farmCalculation.calculatedFootprint.misc.landUseChangeFooprint.reduce((sum, luc) => {
    const biomassEmission = extractEmissionValue(luc.footprints.biomassEmissionFootprint);
    const deadOrganicMatterEmission = extractEmissionValue(luc.footprints.deadOrganicMatterFootprint);
    const socChangeEmission = extractEmissionValue(luc.footprints.socChangeFootprint);
    return sum + biomassEmission + deadOrganicMatterEmission + socChangeEmission;
  }, 0);

  /** PIGLET WASTE & WASTEWATER */
  const totalWasteEmissions = extractCommodityWasteEmission(pigletCalculation.calculatedFootprint.waste);

  const feedingCO2e = extractEmissionValue(feed?.feeding?.feedCO2eq);
  const manureManagementCO2e = extractEmissionValue(husbandry?.manureManagement?.manureCO2eq);
  const stockRenewalCO2e = extractEmissionValue(husbandry?.stockRenewal?.stockRenewalCO2eq);
  const energyCO2e = extractEmissionValue(energy);

  const totalCO2e = sumNumbers([feedingCO2e, manureManagementCO2e, stockRenewalCO2e, energyCO2e, totalWasteEmissions, totalLucEmissions]);

  if (totalCO2e === 0) return null;

  const createPigletDataRow = (sourceEmissions: number): FootprintingPigletResultsTableRowData => ({
    totalCO2e: sourceEmissions,
    perKgYieldCO2e: sourceEmissions / totalKgSalesWeight,
    perSowCO2e: sourceEmissions / totalSows,
    shareOfTotal: _.round(sourceEmissions / totalCO2e, 4),
  });

  const tableData: FootprintingPigletResultsTableData = {
    feeding: { data: createPigletDataRow(feedingCO2e) },
    manureManagement: { data: createPigletDataRow(manureManagementCO2e) },
    stockRenewal: { data: createPigletDataRow(stockRenewalCO2e) },
    energy: { data: createPigletDataRow(energyCO2e) },
    waste: { data: createPigletDataRow(totalWasteEmissions) },
    luc: { data: createPigletDataRow(totalLucEmissions) },
  };

  return {
    overall: {
      totalCO2e,
      perKgYieldCO2e: totalCO2e / totalKgSalesWeight,
      perSowCO2e: totalCO2e / totalSows,
    },
    tableData,
  };
}

/**
 * Helper function that renders the accordion items on footprinting forms
 * Creates each accordion item based on label and content provided in each translation file namespace
 */

export const createAccordionItems = (
  t: (key: string) => string,
  namespace: string,
  pageKey: string,
  sections: readonly string[] = Object.values(ACCORDION_SECTIONS)
) =>
  sections.map((section) => ({
    key: `${pageKey}-${section}`,
    label: t(`footprinting:accordions.titles.${section}.label`),
    content: t(`${namespace}:accordions.${pageKey}.${section}.content`),
  }));

export const isOwnProductionComplete = (values: OwnProductionValues, isAnimalBased: boolean): boolean => {
  if (!values.enabled) return true;
  if (isAnimalBased) return !!values.livestockCount;
  return (
    values.items.length > 0 &&
    values.items.every((item) => {
      if (!(item.biomassInputType && item.quantity && item.quantityUnit)) return false;
      if (item.biomassInputType === "other") return !!item.comment;
      return true;
    })
  );
};

export function createBeefFootprintCalculationSummary(
  _farm: FarmDetailsResponse,
  farmCalculation: CalculatedFarmFootprintResponse | undefined,
  beefCalculation: CalculatedBeefFootprintResponse | undefined,
  beefFootprint: BeefFootprintReturnBody | undefined
): BeefFootprintCalculationSummary | null {
  const cattleCount = beefFootprint?.footprint?.herdSize?.reduce((total: number, h: { cowCount: number }) => total + (h.cowCount ?? 0), 0) ?? 0;
  const beefYieldKgPerAnimal = beefFootprint?.footprint?.beefYieldKgPerAnimal ?? null;

  if (!_farm || !farmCalculation || !beefCalculation || cattleCount === 0 || !beefYieldKgPerAnimal) return null;

  const beefYieldKgs = beefYieldKgPerAnimal * cattleCount;

  /** LAND USE CHANGE */
  const totalLandUseChangeEmissions = farmCalculation.calculatedFootprint.misc.landUseChangeFooprint.reduce((sum, luc) => {
    const biomassEmission = extractEmissionValue(luc.footprints.biomassEmissionFootprint);
    const deadOrganicMatterEmission = extractEmissionValue(luc.footprints.deadOrganicMatterFootprint);
    const socChangeEmission = extractEmissionValue(luc.footprints.socChangeFootprint);
    return sum + biomassEmission + deadOrganicMatterEmission + socChangeEmission;
  }, 0);

  /** BEEF WASTE & WASTEWATER */
  const totalWasteEmissions = extractCommodityWasteEmission(beefCalculation.calculatedFootprint.waste);

  /** BEEF STOCK RENEWAL */
  const totalStockRenewalEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.stockRenewal);

  /** BEEF CALF REARING */
  const calvesDigestionEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.calves?.feeding?.digestionCO2eq);
  const calvesFeedProductionEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.calves?.feeding?.production);
  const calvesManureManagementEmissions = sumNumbers([
    extractEmissionValue(beefCalculation.calculatedFootprint.calves?.manureManagement.storageCO2eq),
    extractEmissionValue(beefCalculation.calculatedFootprint.calves?.manureManagement.storageDirectCO2eq),
    extractEmissionValue(beefCalculation.calculatedFootprint.calves?.manureManagement.storageIndirectCO2eq),
  ]);
  const totalCalfRearingEmissions = sumNumbers([calvesDigestionEmissions, calvesFeedProductionEmissions, calvesManureManagementEmissions]);

  /** BEEF CATTLE FEEDING EMISSIONS */
  const cattleDigestionEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.feeding?.digestionCO2eq);
  const cattleFeedProductionEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.feeding?.production);
  const cattleFeedLossEmissions = sumNumbers([
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.feeding?.feedLossDirectCO2eq),
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.feeding?.feedLossIndirectCO2eq),
  ]);
  const totalCattleFeedingEmissions = sumNumbers([cattleDigestionEmissions, cattleFeedProductionEmissions, cattleFeedLossEmissions]);

  /** BEEF CATTLE MANURE MANAGEMENT */
  const cattleManureStorageEmissions = sumNumbers([
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.manureManagement?.storageCO2eq),
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.manureManagement?.storageIndirectCO2eq),
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.manureManagement?.storageDirectCO2eq),
  ]);
  const cattleManurePastureEmissions = sumNumbers([
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.pasture?.pastureCO2eq),
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.pasture?.pastureDirectCO2eq),
    extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.pasture?.pastureIndirectCO2eq),
  ]);
  const cattleManureBeddingEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.beefCattle?.bedding);
  const totalCattleManureEmissions = sumNumbers([cattleManureStorageEmissions, cattleManurePastureEmissions, cattleManureBeddingEmissions]);

  /** BEEF WATER EMISSIONS */
  const totalWaterEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.waterUsage);

  /** BEEF ENERGY EMISSIONS */
  const nonDieselEnergyEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.energy);
  const dieselEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.diesel);
  const totalEnergyEmissions = sumNumbers([nonDieselEnergyEmissions, dieselEmissions]);

  /** BEEF MACHINERY EMISSIONS */
  const totalMachineryEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.machinery);

  /** BEEF GRASSLAND FERTILISER */
  const totalGrasslandFertiliserEmissions = extractEmissionValue(beefCalculation.calculatedFootprint.misc?.fertiliser);

  const totalCO2e = sumNumbers([
    totalStockRenewalEmissions,
    totalCalfRearingEmissions,
    totalCattleFeedingEmissions,
    totalCattleManureEmissions,
    totalWaterEmissions,
    totalEnergyEmissions,
    totalMachineryEmissions,
    totalWasteEmissions,
    totalLandUseChangeEmissions,
    totalGrasslandFertiliserEmissions,
  ]);

  if (totalCO2e === 0 || beefYieldKgs === 0) return null;

  const createBeefDataRow = (sourceEmissions: number, subData: FootprintingBeefResultsSubDataRowData[] = []): FootprintingBeefResultsDataTableRow => ({
    data: {
      totalCO2e: sourceEmissions,
      perKgYieldCO2e: sourceEmissions / beefYieldKgs,
      perCattleCO2e: sourceEmissions / cattleCount,
      shareOfTotal: _.round(sourceEmissions / totalCO2e, 4),
    },
    subData,
  });

  const buildSubData = (rows: Array<[BeefResultsSubDataRowSource, number]>): FootprintingBeefResultsSubDataRowData[] =>
    rows
      .filter(([, val]) => val !== 0)
      .map(([source, val]) => ({
        source,
        totalCO2e: val,
        perKgYieldCO2e: val / beefYieldKgs,
        perCattleCO2e: val / cattleCount,
        shareOfTotal: _.round(val / totalCO2e, 4),
      }));

  const tableData: FootprintingBeefResultsTableData = {
    stockRenewal: createBeefDataRow(totalStockRenewalEmissions),
    calfRearing: createBeefDataRow(
      totalCalfRearingEmissions,
      buildSubData([
        ["digestion", calvesDigestionEmissions],
        ["feedProduction", calvesFeedProductionEmissions],
        ["manureManagement", calvesManureManagementEmissions],
      ])
    ),
    feeding: createBeefDataRow(
      totalCattleFeedingEmissions,
      buildSubData([
        ["digestion", cattleDigestionEmissions],
        ["feedProduction", cattleFeedProductionEmissions],
        ["feedLoss", cattleFeedLossEmissions],
      ])
    ),
    manure: createBeefDataRow(
      totalCattleManureEmissions,
      buildSubData([
        ["manureStorage", cattleManureStorageEmissions],
        ["manurePasture", cattleManurePastureEmissions],
        ["manureBedding", cattleManureBeddingEmissions],
      ])
    ),
    water: createBeefDataRow(totalWaterEmissions),
    energy: createBeefDataRow(
      totalEnergyEmissions,
      buildSubData([
        ["nonDiesel", nonDieselEnergyEmissions],
        ["diesel", dieselEmissions],
      ])
    ),
    machinery: createBeefDataRow(totalMachineryEmissions),
    waste: createBeefDataRow(totalWasteEmissions),
    luc: createBeefDataRow(totalLandUseChangeEmissions),
    grasslandFertiliser: createBeefDataRow(totalGrasslandFertiliserEmissions),
  };

  return {
    overall: {
      totalCO2e,
      perKgYieldCO2e: totalCO2e / beefYieldKgs,
      perCattleCO2e: totalCO2e / cattleCount,
    },
    tableData,
  };
}

export const isCooperationComplete = (values: CooperationValues): boolean => {
  if (!values.enabled) return true;
  return values.items.length > 0 && values.items.every((item) => item.exportedBiomassType && item.quantity && item.quantityUnit);
};

export const isFertilizerSourceComplete = (values: FertilizerSourceValues, isAnimalBased: boolean): boolean =>
  (values.ownProduction.enabled || values.cooperation.enabled) &&
  isOwnProductionComplete(values.ownProduction, isAnimalBased) &&
  isCooperationComplete(values.cooperation);

export const toFertilizerSourceValues = (productions: FarmOrganicFertilizerProductionEntry[], fertilizerId: string): FertilizerSourceValues => {
  const ownRecord = productions.find((p) => p.fertilizerId === fertilizerId && p.sourceType === "own_production");
  const coopRecord = productions.find((p) => p.fertilizerId === fertilizerId && p.sourceType === "cooperation");

  return {
    ownProduction: ownRecord
      ? {
          enabled: true,
          livestockCount: ownRecord.livestockCount,
          comment: ownRecord.comment,
          items: ownRecord.ownProductionItems.map((item) => ({
            tempId: item.id,
            biomassInputType: item.biomassInputType,
            quantity: item.quantity,
            quantityUnit: item.quantityUnit,
            comment: item.comment,
          })),
        }
      : { enabled: false, livestockCount: null, comment: null, items: [] },
    cooperation: coopRecord
      ? {
          enabled: true,
          items:
            coopRecord.cooperationItems.length > 0
              ? coopRecord.cooperationItems.map((ci) => ({
                  tempId: ci.id,
                  exportedBiomassType: ci.exportedBiomassType,
                  quantity: ci.quantity,
                  quantityUnit: ci.quantityUnit,
                  comment: ci.comment,
                }))
              : // Derived on every render, so the placeholder id must be deterministic to keep form fields stable
                [{ tempId: `${coopRecord.id}-new`, exportedBiomassType: null, quantity: null, quantityUnit: null, comment: null }],
        }
      : { enabled: false, items: [] },
  };
};

export const getSelfProducedFertilizerIdsByYear = (allFieldsPractices: Record<string, FieldPracticeResponse> | undefined): Record<number, string[]> => {
  const idsByYear: Record<number, Set<string>> = {};
  Object.values(allFieldsPractices || {})
    .flatMap((fieldPractices) => fieldPractices.practices)
    .forEach((practice) => {
      if (practice.type !== PRACTICE_SLUG.ORGANIC_FERTILIZERS || practice.selfProduced !== true || !practice.year || !practice.assignedPracticeId) return;
      if (!idsByYear[practice.year]) idsByYear[practice.year] = new Set();
      idsByYear[practice.year].add(practice.assignedPracticeId);
    });
  return Object.fromEntries(Object.entries(idsByYear).map(([year, ids]) => [year, [...ids]]));
};
